import { useCallback } from 'react';
import APIConfig from '@/constants/APIConfig';
import commonService from '@/services/common.service';
import { IAPIResponseType } from '@/models/api.model';
import { ChatMessage } from '@/models/chat.model.ts';
import { Misc } from "@/constants";
import { useDispatch } from 'react-redux';
import { setConvertingProjectId } from '@/store/actions/projectdata.action';
import Communications from '@/services/communications.service';

export const useChatApi = (
    projectId: string,
    appType: string,
    onStreamStart: () => void,
    onStreamEnd: () => void,
    onError: (errorMessage?: string) => void
) => {
    const jwtToken = localStorage.getItem(Misc.LOCAL_STORAGE_JWT_TOKEN);
    const dispatch = useDispatch();


    const updateCodeAfterStream = useCallback(() => {
        // if () {
        // const updatedCode = commonService.getSandpackJsonFromResponse(streamedData)
        // if (isNewApp) {
        //     dispatch(setProjectCode(updatedCode))
        // } else {
        //     dispatch(updateProjectCode(updatedCode))
        // }
        commonService._communications.fetchProjectCode.next(true)
        commonService._communications.setPreviewLoadingStateSubject.next(true);

        // }
    }, [])

    const handleAIOutput = useCallback(async (response: Response) => {
        const reader = response.body?.getReader();
        if (!reader) {
            onError();
            return;
        }
        const decoder = new TextDecoder();
        let buffer = "";
        // let totalBuffer = "";
        commonService._communications.startCodeStreamerSubject.next(true);
        onStreamStart();
        try {
            while (true) {
                const { done, value } = await reader.read();
                if (done) break;

                const chunk = decoder.decode(value, { stream: true });
                buffer += chunk;
                // totalBuffer += chunk;

                let newlineIndex;
                while ((newlineIndex = buffer.indexOf("\n")) !== -1) {
                    const line = buffer.slice(0, newlineIndex).trim();
                    buffer = buffer.slice(newlineIndex + 1);

                    if (!line || !line.startsWith("data:")) continue;

                    try {
                        // Extract the JSON part from the line
                        const jsonStr = line.replace(/^data:\s*/, "");
                        const parsedData = JSON.parse(jsonStr);


                        // Handle error messages directly in the stream
                        if (parsedData.type === "error") {
                            let errorMessage = "Error Processing Request";

                            try {
                                // Parse the nested error object that's in string format
                                const nestedError = JSON.parse(parsedData.message.replace(/'/g, '"'));
                                if (nestedError?.error?.message) {
                                    errorMessage = nestedError.error.message;
                                }
                            } catch (parseErr) {
                                console.error("Error parsing nested error message:", parseErr);
                                // If nested parsing fails, use the original message
                                errorMessage = parsedData.message || errorMessage;
                            }

                            onError(errorMessage);
                            // Don't forward error messages to the socket handler
                            continue;
                        }

                        // Forward normal messages to the socket handler
                        commonService._communications.handleCodeStreamerSubject.next(line);
                    } catch (err) {
                        console.error("Error processing stream line:", line, err);
                    }
                }
            }
            updateCodeAfterStream()

        } catch (error) {
            console.error("Error reading stream:", error);
            onError();
        } finally {
            commonService._communications.endCodeStreamerSubject.next(true);
            onStreamEnd();
        }
    }, [onStreamStart, onStreamEnd, onError, updateCodeAfterStream]);

    const sendChatMessage = useCallback(async (chatInput: string, imageFile?: File | null, chatMode?: boolean) => {
        try {

            const AuthorizationHeaders = { Authorization: "Bearer " + jwtToken };

            const url = APIConfig.AGENT_START.URL(projectId);

            const formData = new FormData();
            formData.append('prompt', chatInput);
            if (imageFile) {
                formData.append('file', imageFile);
            }
            if (typeof chatMode === 'boolean') {
                formData.append('chat_mode', String(chatMode));
            }


            const response = await fetch(url, {
                method: "POST",
                headers: {
                    // NOTE: Do not set 'Content-Type' header when using FormData with fetch.
                    // The browser will automatically set it to 'multipart/form-data' with the correct boundary.
                    ...AuthorizationHeaders,
                },
                body: formData,
            });

            if (!response.ok) {
                // Handle 401 Unauthorized error
                commonService.handleMixpanelTrack("app_builder_send_message_error", {
                    project_id: projectId,
                    message: chatInput,
                    chat_mode: chatMode ? "true" : "false",
                    timestamp: new Date().toISOString()
                });

                if (response.status === 401) {
                    Communications.logoutSubject.next();
                    return false;
                }

                // Attempt to extract error message from the response body
                let errorMessage = `HTTP error ${response.status}`;
                try {
                    const errorData = await response.json();
                    console.log("errorData", errorData);
                    errorMessage = errorData?.detail?.error || errorData?.message || errorMessage;
                } catch (jsonError) {
                    console.error("Error parsing error response:", jsonError);
                    commonService._alert.showToast("Error processing request.", "error")
                }

                throw new Error(errorMessage);
            }

            commonService.handleMixpanelTrack("app_builder_send_message", {
                project_id: projectId,
                message: chatInput,
                chat_mode: chatMode ? "true" : "false",
            })

            commonService._communications.startCodeStreamerSubject.next(true);

            await handleAIOutput(response);
            return true;
        } catch (error: any) {
            onError?.(error.message || "Network error occurred");
            commonService.handleMixpanelTrack("app_builder_send_message_error", {
                project_id: projectId,
            })
            return false;
        }
    }, [projectId, jwtToken, handleAIOutput, onError]);

    const convertAppChatMessage = useCallback(async (old_project_id: string) => {
        try {
            if (projectId !== old_project_id) {
                const AuthorizationHeaders = { Authorization: "Bearer " + jwtToken };
                const url = APIConfig.CONVERT_APP.URL;
                const payload = {
                    old_project_id: old_project_id,
                    new_project_id: projectId,
                    convert_to: 'html-' + appType
                };
                const response = await fetch(url, {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                        ...AuthorizationHeaders,
                    },
                    body: JSON.stringify(payload),
                });

                if (!response.ok) {
                    // Handle 401 Unauthorized error
                    if (response.status === 401) {
                        Communications.logoutSubject.next();
                        return false;
                    }

                    // Attempt to extract error message from the response body
                    let errorMessage = `HTTP error ${response.status}`;
                    try {
                        const errorData = await response.json();
                        errorMessage = errorData?.detail || errorData?.message || errorMessage;
                    } catch (jsonError) {
                        console.error("Error parsing error response:", jsonError);
                        commonService._alert.showToast("Error processing request.", "error")
                    }

                    throw new Error(errorMessage);
                }
                dispatch(setConvertingProjectId(''))
                await handleAIOutput(response);
                return true;
            }
        } catch (error: any) {
            onError?.(error.message || "Network error occurred");
            return false;
        }
    }, [projectId, appType, jwtToken, handleAIOutput, onError, dispatch]);


    const fetchChatHistory = useCallback(async () => {
        try {
            const response: IAPIResponseType<ChatMessage[]> = await commonService._project_service.getProjectChatHistories(projectId);
            return response?.data || [];
        } catch (error) {
            console.error("Error fetching chat histories:", error);
            return [];
        }
    }, [projectId]);

    return {
        sendChatMessage,
        fetchChatHistory,
        convertAppChatMessage
    };
};